<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { toZonedTime } from 'date-fns-tz'

import { useEpgAirTimeHelper } from '../../../../../composables/use-epg-air-time-helper'
import { useChipScroller } from '../../../../../composables/use-chip-scroller'
import { useClient } from '../../../../../utils/client-only'
import Icon from '../../../../../components/shared/Icon.vue'
import CustomAirtime from './CustomAirtime.vue'

import type { FilterState } from '../types'

type DayChip = {
  offset: number,
  label: string,
  dateLabel: string,
  isActive: boolean
}

type SelectOption = {
  value: string,
  label: string
}

const props = withDefaults(defineProps<{
  filtersOpen: boolean,
  selectedFilters: FilterState,
  dayChips: DayChip[],
  timeOptions: SelectOption[],
  categoryOptions: SelectOption[],
  applyDisabled?: boolean,
  filterCount?: number
}>(), {
  applyDisabled: false,
  filterCount: 0
})

const emit = defineEmits<{
  'toggle-filters': [],
  'select-day': [offset: number],
  'select-time': [value: string],
  'update-custom-time': [value: { hours: string, minutes: string }],
  'select-category': [value: string],
  'update:free-tv-only': [value: boolean],
  'reset-filters': [],
  'apply-filters': []
}>()

const { timeZone } = useEpgAirTimeHelper()
const { isClient } = useClient()

const timeRowEl = ref<HTMLElement | null>(null)
const customTimeEl = ref<InstanceType<typeof CustomAirtime> | null>(null)

const customTimePopoverOpen = ref(false)
const customTimeButtonEl = ref<HTMLElement | null>(null)
const customTimePopoverStyle = ref<Record<string, string>>({})

const {
  el: dayChipsEl,
  canScrollPrev: daysCanScrollPrev,
  canScrollNext: daysCanScrollNext,
  isDragging: isDraggingDays,
  consumeDragMoved: consumeDaysDragMoved,
  updateScrollState: updateDaysScrollState,
  scrollByChip: scrollDays,
  scrollActiveChipIntoView: scrollActiveDayIntoView,
  startDrag: startDaysDrag,
  endDrag: endDaysDrag
} = useChipScroller()

const {
  el: categoryChipsEl,
  canScrollPrev: categoriesCanScrollPrev,
  canScrollNext: categoriesCanScrollNext,
  isDragging: isDraggingCategories,
  consumeDragMoved: consumeCategoriesDragMoved,
  updateScrollState: updateCategoriesScrollState,
  scrollByChip: scrollCategories,
  startDrag: startCategoriesDrag,
  endDrag: endCategoriesDrag
} = useChipScroller()

const spinnerHours = computed(() => {
  if (!props.selectedFilters.airTime) return '20'
  const zoned = toZonedTime(new Date(props.selectedFilters.airTime), timeZone)
  return String(zoned.getHours()).padStart(2, '0')
})

const spinnerMinutes = computed(() => {
  if (!props.selectedFilters.airTime) return '15'
  const zoned = toZonedTime(new Date(props.selectedFilters.airTime), timeZone)
  return String(zoned.getMinutes()).padStart(2, '0')
})

function isTimeActive (value: string) {
  if (value === 'custom') return props.selectedFilters.airTimeOptions === 'custom'
  return props.selectedFilters.airTimeOptions === value
}

function isCategoryActive (value: string) {
  if (value === 'all') return !props.selectedFilters.categoryIds?.length
  return props.selectedFilters.categoryIds?.includes(value)
}

function handleTimeClick (value: string, event: MouseEvent) {
  if (value === 'custom') {
    customTimePopoverOpen.value = true
    customTimeButtonEl.value = event.currentTarget as HTMLElement
    nextTick(updateCustomTimePopoverPosition)
  }
  emit('select-time', value)
}

function closeCustomTimePopover () {
  customTimePopoverOpen.value = false
}

// Anchor the popover to the actual 'Uhr anzeigen' button, since its position shifts between breakpoints
function updateCustomTimePopoverPosition () {
  const button = customTimeButtonEl.value
  const container = timeRowEl.value
  const popoverEl = customTimeEl.value?.$el as HTMLElement | undefined
  if (!button || !container || !popoverEl) return

  const buttonRect = button.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const maxLeft = Math.max(0, containerRect.width - popoverEl.offsetWidth)
  const left = Math.min(Math.max(0, buttonRect.left - containerRect.left), maxLeft)
  const top = buttonRect.bottom - containerRect.top + 8

  customTimePopoverStyle.value = { left: `${left}px`, top: `${top}px` }
}

// Desktop custom time popover: clicking anywhere outside the UHRZEIT row closes it again
function handleOutsideCustomTimeClick (event: MouseEvent) {
  if (!customTimePopoverOpen.value) return
  if (event.target instanceof Node && !timeRowEl.value?.contains(event.target)) {
    customTimePopoverOpen.value = false
  }
}

// Suppress the click that follows a drag so a swipe doesn't also select the chip underneath the pointer
function handleDayClick (offset: number) {
  if (consumeDaysDragMoved()) return
  emit('select-day', offset)
}

function handleCategoryClick (value: string) {
  if (consumeCategoriesDragMoved()) return
  emit('select-category', value)
}

function updateScrollStates () {
  updateDaysScrollState()
  updateCategoriesScrollState()
}

watch(() => props.filtersOpen, (isOpen) => {
  if (isOpen) nextTick(() => {
    updateScrollStates()
    scrollActiveDayIntoView()
  })
})

onMounted(() => {
  updateScrollStates()
  window.addEventListener('resize', updateScrollStates)
  window.addEventListener('resize', updateCustomTimePopoverPosition)
  document.addEventListener('click', handleOutsideCustomTimeClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateScrollStates)
  window.removeEventListener('resize', updateCustomTimePopoverPosition)
  document.removeEventListener('click', handleOutsideCustomTimeClick)
  endDaysDrag()
  endCategoriesDrag()
})
</script>

<template>
  <!-- Mobile compact trigger (hidden on desktop via CSS) -->
  <section class="bx-search__header bx-typo--search-header bx-epg-filter__trigger">
    <div class="bx-epg-filter__quicktimes">
      <button type="button"
              class="bx-epg-chip"
              :class="{ 'bx-epg-chip--active': isClient && selectedFilters.airTimeOptions === 'now' }"
              @click="$emit('select-time', 'now')">
        Jetzt
      </button>
      <button type="button"
              class="bx-epg-chip"
              :class="{ 'bx-epg-chip--active': isClient && selectedFilters.airTimeOptions === '20:15' }"
              @click="$emit('select-time', '20:15')">
        20:15
      </button>
      <button type="button"
              class="bx-epg-chip"
              :class="{ 'bx-epg-chip--active': isClient && selectedFilters.airTimeOptions === '22:00' }"
              @click="$emit('select-time', '22:00')">
        22:00
      </button>
    </div>
    <div class="bx-filtertoggle"
         tabindex="0"
         role="button"
         :aria-expanded="filtersOpen"
         :aria-label="filtersOpen ? 'Filter schließen' : 'Filter öffnen'"
         @click="$emit('toggle-filters')"
         @keyup.enter="$emit('toggle-filters')">
      <Icon name="filters" />
      <span v-if="isClient && filterCount > 0"
            class="bx-filter__count">{{ filterCount }}</span>
      <span>Filter<span> {{ filtersOpen ? 'auswählen' : 'anzeigen' }}</span></span>
    </div>
    <button type="button"
            class="bx-epg-filter__reset-link"
            @click="$emit('reset-filters')">
      Filter zurücksetzen
    </button>
  </section>

  <!-- Full filter panel -->
  <div class="bx-filters__wrapper bx-epg-filter">
    <!-- DATUM row -->
    <div class="bx-epg-filter__row">
      <span class="bx-epg-filter__row-label">Datum</span>
      <div class="bx-epg-filter__row-content bx-epg-filter__row-content--date">
        <button type="button"
                class="bx-epg-filter__nav-btn"
                aria-label="Vorherigen Tag anzeigen"
                :disabled="isClient && !daysCanScrollPrev"
                @click="scrollDays(-1)">
          <Icon name="arrow_pager-prev" />
        </button>
        <div ref="dayChipsEl"
             class="bx-epg-filter__chips bx-epg-filter__chips--days"
             :class="{ 'bx-epg-filter__chips--dragging': isDraggingDays }"
             @scroll="updateDaysScrollState"
             @pointerdown="startDaysDrag">
          <button v-for="chip in dayChips"
                  :key="chip.offset"
                  type="button"
                  class="bx-epg-chip bx-epg-chip--day"
                  :class="{ 'bx-epg-chip--active': isClient && chip.isActive }"
                  @click="handleDayClick(chip.offset)">
            <span class="bx-epg-chip__day-name">{{ chip.label }}</span>
            <span class="bx-epg-chip__day-date">{{ chip.dateLabel }}</span>
          </button>
        </div>
        <button type="button"
                class="bx-epg-filter__nav-btn"
                aria-label="Nächsten Tag anzeigen"
                :disabled="isClient && !daysCanScrollNext"
                @click="scrollDays(1)">
          <Icon name="arrow_pager-next" />
        </button>
      </div>
    </div>

    <!-- UHRZEIT row -->
    <div class="bx-epg-filter__row">
      <span class="bx-epg-filter__row-label">Uhrzeit</span>
      <div ref="timeRowEl"
           class="bx-epg-filter__row-content bx-epg-filter__row-content--time">
        <div class="bx-epg-filter__chips">
          <button v-for="opt in timeOptions"
                  :key="opt.value"
                  type="button"
                  class="bx-epg-chip"
                  :class="{
                    'bx-epg-chip--active': isClient && isTimeActive(opt.value),
                    'bx-epg-chip--hide-mobile': opt.value !== 'now' && opt.value !== '20:15' && opt.value !== '22:00'
                  }"
                  @click="handleTimeClick(opt.value, $event)">
            {{ opt.label }}
          </button>
        </div>
        <!-- Custom time spinner: always visible on mobile, shown as a popover on demand on desktop -->
        <CustomAirtime ref="customTimeEl"
                       class="bx-epg-filter__custom-time"
                       :style="customTimePopoverStyle"
                       :class="{
                         'bx-epg-filter__custom-time--visible': isClient && selectedFilters.airTimeOptions === 'custom',
                         'bx-epg-filter__custom-time--collapsed': isClient && !customTimePopoverOpen
                       }"
                       :hours="isClient ? spinnerHours : '20'"
                       :minutes="isClient ? spinnerMinutes : '15'"
                       @update:time="$emit('update-custom-time', $event)"
                       @close="closeCustomTimePopover" />
      </div>
    </div>

    <!-- KATEGORIEN row -->
    <div class="bx-epg-filter__row">
      <span class="bx-epg-filter__row-label">Kategorie</span>
      <div class="bx-epg-filter__row-content bx-epg-filter__row-content--categories">
        <button type="button"
                class="bx-epg-filter__nav-btn"
                aria-label="Vorherige Kategorien anzeigen"
                :disabled="isClient && !categoriesCanScrollPrev"
                @click="scrollCategories(-1)">
          <Icon name="arrow_pager-prev" />
        </button>
        <div ref="categoryChipsEl"
             class="bx-epg-filter__chips bx-epg-filter__chips--categories"
             :class="{ 'bx-epg-filter__chips--dragging': isDraggingCategories }"
             @scroll="updateCategoriesScrollState"
             @pointerdown="startCategoriesDrag">
          <button v-for="opt in categoryOptions"
                  :key="opt.value"
                  type="button"
                  class="bx-epg-chip"
                  :class="{ 'bx-epg-chip--active': isClient && isCategoryActive(opt.value) }"
                  @click="handleCategoryClick(opt.value)">
            {{ opt.label }}
          </button>
        </div>
        <button type="button"
                class="bx-epg-filter__nav-btn"
                aria-label="Weitere Kategorien anzeigen"
                :disabled="isClient && !categoriesCanScrollNext"
                @click="scrollCategories(1)">
          <Icon name="arrow_pager-next" />
        </button>

        <label class="bx-epg-filter__freetv">
          <span>Nur Free-TV</span>
          <input type="checkbox"
                 :checked="selectedFilters.freeTvOnly"
                 @change="$emit('update:free-tv-only', ($event.target as HTMLInputElement).checked)">
        </label>
      </div>
    </div>

    <!-- Actions -->
    <div class="bx-epg-filter__actions">
      <button type="button"
              class="bx-epg-filter__reset-link"
              @click="$emit('reset-filters')">
        Filter zurücksetzen
      </button>
      <button type="button"
              class="bx-form__button"
              :disabled="applyDisabled"
              @click="$emit('apply-filters')">
        Ergebnisse zeigen
      </button>
    </div>
  </div>
</template>
